import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import pluginsService, {
  type PluginListResponse,
  type OrganizationPluginListResponse,
  type EnablePluginRequest,
  type DisablePluginRequest,
  type UpdatePluginConfigRequest,
} from '@/services/plugins.service';

/**
 * Tüm mevcut plugin'leri getir
 */
export function usePlugins() {
  return useQuery({
    queryKey: ['plugins'],
    queryFn: () => pluginsService.getPlugins(),
  });
}

/**
 * Organization için plugin'leri getir
 */
export function useOrganizationPlugins(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['organizationPlugins', organizationId],
    queryFn: () => pluginsService.getOrganizationPlugins(organizationId!),
    enabled: !!organizationId,
  });
}

/**
 * Plugin aktifleştirme mutation
 */
export function useEnablePlugin(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EnablePluginRequest) =>
      pluginsService.enablePlugin(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationPlugins', organizationId] });
    },
  });
}

/**
 * Plugin deaktifleştirme mutation
 */
export function useDisablePlugin(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DisablePluginRequest) =>
      pluginsService.disablePlugin(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationPlugins', organizationId] });
    },
  });
}

/**
 * Plugin config güncelleme mutation
 */
export function useUpdatePluginConfig(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePluginConfigRequest) =>
      pluginsService.updatePluginConfig(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationPlugins', organizationId] });
    },
  });
}
